import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TuiAlertService, TuiButton, TuiHint, TuiLoader, TuiTextfield } from '@taiga-ui/core';
import { TuiInputModule, TuiInputDateRangeModule, TuiTextfieldControllerModule } from '@taiga-ui/legacy';
import { TuiTable } from '@taiga-ui/addon-table';
import { TuiDay, TuiDayRange } from '@taiga-ui/cdk';
import { TuiSearch } from '@taiga-ui/layout';
import printJS from 'print-js';
import * as XLSX from 'xlsx';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { ReportsService, ReportData } from '../../../core/services/reports.service';
import { ClientArchivedBadgeComponent } from '../../../shared/components/client-archived-badge/client-archived-badge.component';
import { reportArchivedStatus } from '../report-export.util';

@Component({
  selector: 'app-non-registered-clients-report',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TuiTextfield,
    TuiButton,
    TuiHint,
    TuiLoader,
    TuiInputModule,
    TuiInputDateRangeModule,
    TuiTextfieldControllerModule,
    TuiTable,
    TuiSearch,
    ClientArchivedBadgeComponent,
  ],
  templateUrl: './non-registered-clients-report.component.html',
  styleUrl: './non-registered-clients-report.component.scss',
  providers: [DatePipe],
})
export class NonRegisteredClientsReportComponent implements OnInit, OnDestroy {
  private reportsService = inject(ReportsService);
  private alerts = inject(TuiAlertService);
  private datePipe = inject(DatePipe);

  reportData: ReportData[] = [];
  filteredData: ReportData[] = [];
  loading = false;

  // Filter Type
  rangeFilterTypes: string[] = ['Monthly', 'Custom Range'];
  rangeFilterControl = new FormControl<string>('Monthly');

  // Filters
  searchControl = new FormControl('');
  monthControl = new FormControl<number>(new Date().getMonth());
  yearControl = new FormControl<number>(new Date().getFullYear());
  customRangeControl = new FormControl<TuiDayRange | null>(null);
  selectedMonth: Date | null = null;
  selectedDateRange: { startDate: Date; endDate: Date } | null = null;

  // Month options
  months = [
    { value: 0, label: 'January' },
    { value: 1, label: 'February' },
    { value: 2, label: 'March' },
    { value: 3, label: 'April' },
    { value: 4, label: 'May' },
    { value: 5, label: 'June' },
    { value: 6, label: 'July' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'October' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' },
  ];

  // Year options (last 5 years to next 2 years)
  years: number[] = [];

  generateYears(): void {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 5; i <= currentYear + 2; i++) {
      this.years.push(i);
    }
  }

  // Pagination
  currentPage = 1;
  pageSize = 100;
  totalRecords = 0;
  totalPages = 0;

  // Totals
  totalAdditionalCharges = 0;
  totalShiftCharges = 0;
  grandTotal = 0;

  // Active report subscription (used to cancel previous requests)
  private reportSub: Subscription | null = null;

  ngOnInit(): void {
    // Generate years array
    this.generateYears();

    // Set default to current month
    const today = new Date();
    this.selectedMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    this.monthControl.setValue(today.getMonth());
    this.yearControl.setValue(today.getFullYear());

    this.setupRangeFilterTypeListener();
    this.loadReportData();
  }

  ngOnDestroy(): void {
    if (this.reportSub) {
      this.reportSub.unsubscribe();
      this.reportSub = null;
    }
  }

  applyFilters(): void {
    // Update selected values based on filter type
    const filterType = this.rangeFilterControl.value;
    
    if (filterType === 'Monthly') {
      const month = this.monthControl.value;
      const year = this.yearControl.value;
      if (month !== null && year !== null) {
        this.selectedMonth = new Date(year, month, 1);
        this.selectedDateRange = null;
      }
    } else if (filterType === 'Custom Range' && this.customRangeControl.value) {
      const value = this.customRangeControl.value;
      const from = value.from;
      const to = value.to;
      if (from && to) {
        this.selectedDateRange = {
          startDate: new Date(from.year, from.month, from.day),
          endDate: new Date(to.year, to.month, to.day),
        };
        this.selectedMonth = null;
      }
    }
    
    this.currentPage = 1;
    this.loadReportData();
  }

  setupRangeFilterTypeListener(): void {
    this.rangeFilterControl.valueChanges.subscribe((value) => {
      if (value === 'Monthly') {
        // Reset to current month when switching to Monthly
        const today = new Date();
        this.monthControl.setValue(today.getMonth());
        this.yearControl.setValue(today.getFullYear());
        this.customRangeControl.setValue(null);
      } else if (value === 'Custom Range') {
        this.customRangeControl.setValue(null);
      }
    });
  }

  loadReportData(): void {
    this.loading = true;

    let filters: any = {
      searchText: this.searchControl.value || '',
    };

    if (this.rangeFilterControl.value === 'Monthly' && this.selectedMonth) {
      filters.month = this.selectedMonth.getMonth() + 1;
      filters.year = this.selectedMonth.getFullYear();
    } else if (this.rangeFilterControl.value === 'Custom Range' && this.selectedDateRange) {
      filters.startDate = this.selectedDateRange.startDate.toISOString();
      filters.endDate = this.selectedDateRange.endDate.toISOString();
    }

    const payload = {
      pageIndex: this.currentPage,
      pageSize: this.pageSize,
      filters,
    };

    // Cancel previous in-flight report request if any
    if (this.reportSub) {
      try {
        this.reportSub.unsubscribe();
      } catch (e) {
        // ignore errors from unsubscribe
      }
      this.reportSub = null;
    }

    this.reportSub = this.reportsService.getNonRegisteredClientsReport(payload).subscribe({
      next: (response: any) => {
        if (response?.success && response?.data) {
          this.reportData = response.data.reportData.map((item: any) => ({
            ...item,
            invoiceDate: new Date(item.invoiceDate),
          }));
          this.filteredData = this.reportData;

          this.totalShiftCharges = response.data.totals.totalShiftCharges || 0;
          this.totalAdditionalCharges = response.data.totals.totalAdditionalCharges || 0;
          this.grandTotal = response.data.totals.grandTotal || 0;

          this.totalRecords = response.data.pagination.total || 0;
          this.totalPages = response.data.pagination.totalPages || 0;
        } else {
          this.reportData = [];
          this.filteredData = [];
          this.totalShiftCharges = 0;
          this.totalAdditionalCharges = 0;
          this.grandTotal = 0;
          this.totalRecords = 0;
          this.totalPages = 0;
        }
        this.loading = false;
        // clear completed subscription reference
        if (this.reportSub) {
          this.reportSub.unsubscribe();
          this.reportSub = null;
        }
      },
      error: (error) => {
        console.error('Error loading report:', error);
        this.loading = false;
        this.alerts
          .open('Error loading report data', { appearance: 'error' })
          .subscribe();
        if (this.reportSub) {
          this.reportSub.unsubscribe();
          this.reportSub = null;
        }
      },
    });
  }

  // Invoice numbers and totals are generated server-side for this report.

  clearFilters(): void {
    this.searchControl.setValue('');
    this.rangeFilterControl.setValue('Monthly');
    
    // Set default to current month
    const today = new Date();
    this.selectedMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    this.monthControl.setValue(today.getMonth());
    this.yearControl.setValue(today.getFullYear());
    this.customRangeControl.setValue(null);
    this.selectedDateRange = null;
    this.currentPage = 1;
    this.loadReportData();
  }

  printReport(): void {
    setTimeout(() => {
      printJS({
        printable: 'printable-report',
        type: 'html',
        css: ['styles.css'],
        targetStyles: ['*'],
        documentTitle: 'Non Registered Clients Report',
        header: this.getPrintHeader(),
      });
    }, 300);
  }

  exportToExcel(): void {
    // Determine date range text
    let dateRangeText = '';
    if (this.rangeFilterControl.value === 'Monthly' && this.selectedMonth) {
      dateRangeText = this.datePipe.transform(this.selectedMonth, 'MMMM yyyy') || '';
    } else if (this.rangeFilterControl.value === 'Custom Range' && this.selectedDateRange) {
      const startStr = this.datePipe.transform(this.selectedDateRange.startDate, 'MM/dd/yyyy');
      const endStr = this.datePipe.transform(this.selectedDateRange.endDate, 'MM/dd/yyyy');
      dateRangeText = `${startStr} - ${endStr}`;
    } else {
      const today = new Date();
      dateRangeText = this.datePipe.transform(today, 'MMMM yyyy') || '';
    }
    const generated = this.datePipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');

    // Prepare data starting from row 6
    const data = this.filteredData.map((item) => ({
      'Invoice Date': this.datePipe.transform(item.invoiceDate, 'MM/dd/yyyy'),
      'Invoice Number': item.invoiceNumber,
      'Client Ref No': item.clientRefNo,
      Status: reportArchivedStatus(item.clientArchived),
      'Client Name': item.clientName,
      'With Home Nursing': (item.shiftCharges || 0).toFixed(2),
      'Without Home Nursing': (item.additionalCharges || 0).toFixed(2),
      Total: (item.total || 0).toFixed(2),
    }));

    // Add totals row
    data.push({
      'Invoice Date': '',
      'Invoice Number': '',
      'Client Ref No': '',
      Status: '',
      'Client Name': 'TOTALS',
      'With Home Nursing': this.totalShiftCharges.toFixed(2),
      'Without Home Nursing': this.totalAdditionalCharges.toFixed(2),
      Total: this.grandTotal.toFixed(2),
    });

    // Create empty worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([]);
    XLSX.utils.sheet_add_json(worksheet, data, { origin: 'A6' });

    // Set column widths
    worksheet['!cols'] = [
      { wch: 15 }, // Invoice Date
      { wch: 20 }, // Invoice Number
      { wch: 15 }, // Client Ref No
      { wch: 12 }, // Status
      { wch: 30 }, // Client Name
      { wch: 20 }, // With Home Nursing
      { wch: 22 }, // Without Home Nursing
      { wch: 14 }, // Total
    ];

    // Add header rows (rows 1-4)
    const headerRows = [
      ['ROYAL HOSPITAL - HOME NURSING CARE'],
      ['Non Registered Clients Report'],
      [`Date Range: ${dateRangeText}`],
      [`Generated: ${generated}`],
    ];
    XLSX.utils.sheet_add_aoa(worksheet, headerRows, { origin: 'A1' });

    // Merge cells for headers
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 7 } }, // Row 1
      { s: { r: 1, c: 0 }, e: { r: 1, c: 7 } }, // Row 2
      { s: { r: 2, c: 0 }, e: { r: 2, c: 7 } }, // Row 3
      { s: { r: 3, c: 0 }, e: { r: 3, c: 7 } }, // Row 4
    ];

    // Style helper function
    const setCellStyle = (cell: string, bold: boolean, size: number) => {
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold, sz: size },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      }
    };

    // Apply styles to header rows
    setCellStyle('A1', true, 16);
    setCellStyle('A2', true, 14);
    setCellStyle('A3', true, 12);
    setCellStyle('A4', true, 12);

    // Create workbook and write file
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Non Registered Clients');
    XLSX.writeFile(workbook, `Non_Registered_Clients_Report_${Date.now()}.xlsx`);
  }

  getPrintHeader(): string {
    const dateRange = this.selectedMonth
      ? this.datePipe.transform(this.selectedMonth, 'MMMM yyyy')
      : 'All Periods';
    const generated = this.datePipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');

    return `
      <div style="text-align: center; margin-bottom: 20px;">
        <h2 style="margin: 0; color: #7B68EE;">ROYAL HOSPITAL</h2>
        <h3 style="margin: 5px 0; color: #9370DB;">HOME NURSING CARE</h3>
        <h4 style="margin: 10px 0;">Non Registered Clients Report</h4>
        <p style="margin: 5px 0;">Date Range: ${dateRange}</p>
        <p style="margin: 5px 0; font-size: 12px;">Generated: ${generated}</p>
      </div>
    `;
  }
}
